const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function parseDateLiteral(dateStr: string): Date {
  if (!dateStr) return new Date(NaN);
  const s = String(dateStr).trim();
  if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(s)) {
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(s)) {
    const [m, d, y] = s.split('/').map(Number);
    return new Date(y, m - 1, d);
  }
  const monthYear = s.match(/^(\w+)\s+(\d{4})$/);
  if (monthYear) {
    const mi = MONTHS.findIndex((m) => monthYear[1].startsWith(m));
    if (mi >= 0) return new Date(Number(monthYear[2]), mi, 1);
  }
  const parts = s.split(/[-/T]/);
  if (parts.length >= 3) {
    return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  }
  return new Date(NaN);
}

export function formatDateLabel(date: Date): string {
  if (isNaN(date.getTime())) return '';
  return `${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
}

export function isDateLabel(label: string): boolean {
  if (!label) return false;
  const s = String(label).trim();
  return /^\d{4}-\d{1,2}-\d{1,2}/.test(s) || /^\d{1,2}\/\d{1,2}\/\d{4}/.test(s);
}

export function getDateXAxisConfig(labels: string[]) {
  if (!labels || labels.length < 2) return {};
  const dates = labels.map(parseDateLiteral).filter((d) => !isNaN(d.getTime()));
  if (dates.length < 2) return {};
  const rangeYears =
    (dates[dates.length - 1].getTime() - dates[0].getTime()) / (365.25 * 24 * 60 * 60 * 1000);
  const showMonths: Set<number> = rangeYears >= 2 ? new Set([0, 6]) : new Set([0, 4, 8]);
  return {
    callback: function (_value: any, index: number) {
      const d = parseDateLiteral(labels[index]);
      if (isNaN(d.getTime())) return null;
      if (!showMonths.has(d.getMonth())) return null;

      for (let i = index - 1; i >= 0; i--) {
        const prev = parseDateLiteral(labels[i]);
        if (isNaN(prev.getTime())) continue;
        if (prev.getFullYear() === d.getFullYear() && prev.getMonth() === d.getMonth()) {
          return null;
        }
        break;
      }

      return formatDateLabel(d);
    },
    autoSkip: false,
    maxRotation: 0,
  };
}

/**
 * True when the visible window extends past the 3-year mark, i.e. the earliest
 * visible label is older than (current date − 3yr). Assumes labels are ascending.
 */
export function isPastThreeYearMark(visibleLabels: (string | number)[]): boolean {
  if (!visibleLabels || visibleLabels.length === 0) return false;
  const cutoff = new Date();
  cutoff.setFullYear(cutoff.getFullYear() - 3);
  const earliest = parseDateLiteral(String(visibleLabels[0]));
  return !isNaN(earliest.getTime()) && earliest < cutoff;
}

/**
 * True when the visible window SPANS 3 years or less (latest − earliest ≤ 3yr),
 * regardless of where on the timeline it sits. Used to decide whether to show
 * value labels (labels show for any ≤3-year zoom window). Assumes ascending labels.
 */
export function isWithinThreeYearRange(visibleLabels: (string | number)[]): boolean {
  if (!visibleLabels || visibleLabels.length === 0) return false;
  const first = parseDateLiteral(String(visibleLabels[0]));
  const last = parseDateLiteral(String(visibleLabels[visibleLabels.length - 1]));
  if (isNaN(first.getTime()) || isNaN(last.getTime())) return false;
  const firstPlus3 = new Date(first);
  firstPlus3.setFullYear(first.getFullYear() + 3);
  return last <= firstPlus3;
}

/**
 * X-axis label rule for inventory-change charts:
 *  - yearOnly=false (window within last 3 years): show every 6 months as "MMM yyyy".
 *  - yearOnly=true  (window dragged past the 3-year mark): show every year as the
 *    year only (no month) for the WHOLE axis, so it stays consistent.
 *  - Everything else: hidden ('').
 */
export function inventoryChangeAxisLabel(date: Date, yearOnly: boolean): string {
  if (isNaN(date.getTime())) return '';
  const month = date.getMonth();
  if (yearOnly) {
    // once per year (January), year only
    return month === 0 ? String(date.getFullYear()) : '';
  }
  // every 6 months → Jan / Jul, with month + year
  return month % 6 === 0 ? `${MONTHS[month]} ${date.getFullYear()}` : '';
}

export function getSmartYAxisConfig(allDataValues: number[]) {
  const vals = (allDataValues || []).filter((v) => v != null && !isNaN(v));
  if (vals.length === 0) return {};
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  if (min === max) {
    const pad = Math.abs(min) * 0.1 || 1;
    return { min: min - pad, max: max + pad };
  }
  const range = max - min;
  const padding = range * 0.10;
  const paddedMin = min - padding;
  const paddedMax = max + padding;
  const roughStep = (paddedMax - paddedMin) / 4;
  const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
  const residual = roughStep / magnitude;
  let niceStep: number;
  if (residual <= 1) niceStep = magnitude;
  else if (residual <= 2) niceStep = 2 * magnitude;
  else if (residual <= 2.5) niceStep = 2.5 * magnitude;
  else if (residual <= 5) niceStep = 5 * magnitude;
  else niceStep = 10 * magnitude;
  const niceMin = Math.floor(paddedMin / niceStep) * niceStep;
  const niceMax = Math.ceil(paddedMax / niceStep) * niceStep;
  return { min: niceMin, max: niceMax, ticks: { stepSize: niceStep } };
}
